/**
 * Scan & Trace — /scan. A wedge scanner types into the box and presses Enter; a phone camera reads
 * the code through the browser's BarcodeDetector when it has one; a person can type. The code is
 * resolved on the server (under the user's rights, logged as a scan event) and shown with its facts,
 * next steps and — for lots and items — the chain of custody. A QR printed on a label carries
 * /scan?c=<code>, so opening it from a phone camera lands here with the code already resolved.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.ts';
import { useI18n } from '../lib/i18n.tsx';
import { useRoute } from '../lib/router.tsx';
import { Button, Card, useToast } from '../components/ui.tsx';
import { Icon } from '../components/Icon.tsx';
import { PageHead } from '../components/PageHead.tsx';
import { ScanResultCard, type ScanResult } from '../components/Trace.tsx';

type Detector = { detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue: string }>> };
type DetectorCtor = new (opts?: { formats?: string[] }) => Detector;
const detectorCtor = (): DetectorCtor | null => ((window as unknown as { BarcodeDetector?: DetectorCtor }).BarcodeDetector ?? null);

export function ScanPage() {
  const { t } = useI18n();
  const { query, setQuery } = useRoute();
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [recent, setRecent] = useState<ScanResult[]>([]);
  const [camera, setCamera] = useState<'off' | 'on' | 'unsupported' | 'denied'>('off');
  const input = useRef<HTMLInputElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<number | null>(null);

  const resolve = async (code: string, source: 'screen' | 'camera' | 'wedge' = 'screen') => {
    const c = code.trim();
    if (!c || busy) return;
    setBusy(true);
    try {
      const r = await api.post<ScanResult>('scan', { code: c, source, purpose: 'lookup' });
      setResult(r);
      setRecent((list) => [r, ...list.filter((x) => x.code !== r.code)].slice(0, 12));
      setText('');
      if (query.get('c') !== c) setQuery({ c }, { replace: true });
    } catch (e) { toast.error(e); } finally { setBusy(false); setTimeout(() => input.current?.focus(), 50); }
  };

  // a QR opened from a phone camera lands here with the code in the URL
  const fromUrl = query.get('c');
  useEffect(() => { if (fromUrl && (!result || result.code !== fromUrl)) void resolve(fromUrl); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fromUrl]);
  useEffect(() => { input.current?.focus(); return stopCamera; }, []);

  function stopCamera() {
    if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
    stream.current?.getTracks().forEach((tr) => tr.stop()); stream.current = null;
    setCamera((c) => (c === 'on' ? 'off' : c));
  }
  const startCamera = async () => {
    const Ctor = detectorCtor();
    if (!Ctor || !navigator.mediaDevices?.getUserMedia) { setCamera('unsupported'); return; }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      stream.current = s; setCamera('on');
      const detector = new Ctor({ formats: ['qr_code', 'code_128', 'ean_13', 'code_39'] });
      setTimeout(() => { if (video.current) { video.current.srcObject = s; void video.current.play(); } }, 0);
      timer.current = window.setInterval(async () => {
        if (!video.current || video.current.readyState < 2) return;
        try {
          const codes = await detector.detect(video.current);
          if (codes.length) { stopCamera(); void resolve(codes[0].rawValue, 'camera'); }
        } catch { /* keep looking */ }
      }, 350);
    } catch { setCamera('denied'); }
  };

  return (
    <div className="stack">
      <PageHead title={<><Icon name="ScanLine" size={22} className="muted" />{t('trace.title')}</>} sub={t('trace.intro')} />
      <Card>
        <div className="scan-box">
          <Icon name="QrCode" className="muted" />
          <input ref={input} className="input" value={text} placeholder={t('trace.placeholder')} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void resolve(text, 'wedge'); } }} autoComplete="off" spellCheck={false} data-scan-input />
          <Button variant="primary" icon="Search" busy={busy} onClick={() => void resolve(text)} data-scan-go>{t('trace.resolve')}</Button>
          {camera === 'on' ? <Button icon="X" onClick={stopCamera}>{t('trace.stopCamera')}</Button> : <Button icon="Camera" onClick={() => void startCamera()} data-scan-camera>{t('trace.camera')}</Button>}
        </div>
        {camera === 'unsupported' && <div className="banner amber" style={{ marginTop: 10 }}><Icon name="Info" /><span>{t('trace.cameraUnsupported')}</span></div>}
        {camera === 'denied' && <div className="banner amber" style={{ marginTop: 10 }}><Icon name="Info" /><span>{t('trace.cameraDenied')}</span></div>}
        {camera === 'on' && <div className="scan-camera" style={{ marginTop: 10 }}><video ref={video} muted playsInline /><div className="reticle" /></div>}
      </Card>
      {result && <ScanResultCard result={result} />}
      {recent.length > 1 && (
        <Card title={t('trace.recent')} flush>
          <div className="scan-recent" style={{ padding: 12 }}>
            {recent.map((r) => <button type="button" key={r.code} className="btn ghost sm" onClick={() => setResult(r)}><Icon name={r.found ? 'Check' : 'X'} size={14} className={r.found ? '' : 'muted'} /><span className="mono">{r.code}</span><span className="muted truncate">{r.title ?? ''}</span></button>)}
          </div>
        </Card>
      )}
    </div>
  );
}
